import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { usePlays } from '../hooks/usePlays'
import { ProductionType } from '../types'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'

const PRODUCTION_TYPE_OPTIONS: { value: ProductionType; label: string }[] = [
  { value: 'live', label: 'Live Performance' },
  { value: 'filmed_stage', label: 'Filmed Stage Production' },
  { value: 'feature_film', label: 'Feature Film' },
  { value: 'television', label: 'Television' },
  { value: 'audio', label: 'Audio / Radio' },
  { value: 'immersive', label: 'Immersive / Site-Specific' },
  { value: 'adaptation', label: 'Adaptation' },
]

const SELECT_CLASS =
  'w-full rounded-md border border-stone-700 bg-stone-800 px-3 py-2 text-sm text-stone-100 focus:outline-none focus:ring-1 focus:ring-red-800'

export function AddProductionPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { plays, loading: playsLoading } = usePlays()

  const [playId, setPlayId] = useState(searchParams.get('play_id') ?? '')
  const [title, setTitle] = useState('')
  const [type, setType] = useState<ProductionType>('feature_film')
  const [year, setYear] = useState('')
  const [director, setDirector] = useState('')
  const [companyOrStudio, setCompanyOrStudio] = useState('')
  const [venueOrPlatform, setVenueOrPlatform] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!playId) {
      setError('Please select a play.')
      return
    }
    if (!title.trim()) {
      setError('Please enter a production title.')
      return
    }

    setSubmitting(true)
    setError(null)

    const { error: supabaseError } = await supabase.from('productions').insert({
      play_id: playId,
      title: title.trim(),
      type,
      year: year ? parseInt(year, 10) : null,
      director: director.trim() || null,
      company_or_studio: companyOrStudio.trim() || null,
      venue_or_platform: venueOrPlatform.trim() || null,
      created_by_user_id: user?.id ?? null,
    })

    setSubmitting(false)

    if (supabaseError) {
      setError(supabaseError.message)
    } else {
      navigate(`/plays/${playId}`)
    }
  }

  return (
    <div className="min-h-screen bg-stone-900 px-6 py-10 text-stone-100">
      <div className="mx-auto max-w-xl">
        <h1 className="mb-8 font-serif text-3xl font-semibold tracking-tight">
          Add Production
        </h1>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="play" className="text-stone-300">
              Play
            </Label>
            {playsLoading ? (
              <p className="text-sm text-stone-400">Loading plays…</p>
            ) : (
              <select
                id="play"
                value={playId}
                onChange={(e) => setPlayId(e.target.value)}
                className={SELECT_CLASS}
              >
                <option value="">Select a play…</option>
                {plays.map((play) => (
                  <option key={play.id} value={play.id}>
                    {play.title}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-stone-300">
              Production title
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Hamlet — National Theatre, 2015"
              className="border-stone-700 bg-stone-800 text-stone-100 placeholder:text-stone-500"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="type" className="text-stone-300">
              Type
            </Label>
            <select
              id="type"
              value={type}
              onChange={(e) => setType(e.target.value as ProductionType)}
              className={SELECT_CLASS}
            >
              {PRODUCTION_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="year" className="text-stone-300">
              Year <span className="text-stone-500">(optional)</span>
            </Label>
            <Input
              id="year"
              type="number"
              min="1600"
              max="2100"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              placeholder="e.g. 2015"
              className="border-stone-700 bg-stone-800 text-stone-100 placeholder:text-stone-500"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="director" className="text-stone-300">
              Director <span className="text-stone-500">(optional)</span>
            </Label>
            <Input
              id="director"
              value={director}
              onChange={(e) => setDirector(e.target.value)}
              placeholder="e.g. Kenneth Branagh"
              className="border-stone-700 bg-stone-800 text-stone-100 placeholder:text-stone-500"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="company" className="text-stone-300">
              Company / Studio <span className="text-stone-500">(optional)</span>
            </Label>
            <Input
              id="company"
              value={companyOrStudio}
              onChange={(e) => setCompanyOrStudio(e.target.value)}
              placeholder="e.g. National Theatre"
              className="border-stone-700 bg-stone-800 text-stone-100 placeholder:text-stone-500"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="venue" className="text-stone-300">
              Venue / Platform <span className="text-stone-500">(optional)</span>
            </Label>
            <Input
              id="venue"
              value={venueOrPlatform}
              onChange={(e) => setVenueOrPlatform(e.target.value)}
              placeholder="e.g. Barbican Theatre, NT Live, Disney+"
              className="border-stone-700 bg-stone-800 text-stone-100 placeholder:text-stone-500"
            />
          </div>

          {error && (
            <p className="rounded-md bg-red-950 px-3 py-2 text-sm text-red-400">
              {error}
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-red-900 text-stone-100 hover:bg-red-800"
            >
              {submitting ? 'Saving…' : 'Add Production'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(-1)}
              className="text-stone-400 hover:text-stone-200"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
